import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronRight,
  ShieldAlert,
  Wifi,
  WrenchIcon,
  Inbox,
} from "lucide-react";
import { MobileAppHeader } from "@/components/mobile/MobileAppHeader";
import {
  MCard,
  MEmpty,
  MSectionLabel,
  MStatusPill,
  MTile,
} from "@/components/mobile/ui/primitives";
import { useDevices } from "@/hooks/useDevices";
import { useSoftwareUpdatePolicies } from "@/hooks/useSoftwareUpdates";
import { useApprovalsInbox } from "@/hooks/useApprovals";
import { humanizeAction } from "@/services/approvalsApi";
import { cn } from "@/lib/utils";

function ComplianceRing({ value }: { value: number }) {
  const pct = Math.max(0, Math.min(100, Math.round(value)));
  const r = 40;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - pct / 100);
  const tone =
    pct >= 90
      ? "var(--m-ok)"
      : pct >= 70
        ? "var(--m-warn)"
        : "var(--m-crit)";
  return (
    <div className="relative h-24 w-24 shrink-0">
      <svg
        viewBox="0 0 96 96"
        className="h-full w-full -rotate-90"
        aria-hidden
      >
        <circle
          cx="48"
          cy="48"
          r={r}
          stroke="var(--m-line)"
          strokeWidth="6"
          fill="none"
        />
        <circle
          cx="48"
          cy="48"
          r={r}
          stroke={tone}
          strokeWidth="6"
          strokeLinecap="round"
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset .6s ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-[22px] font-bold tracking-tight text-[color:var(--m-ink)]">
          {pct}%
        </span>
        <span className="text-[9px] font-bold uppercase tracking-wider text-[color:var(--m-gray)]">
          Compliant
        </span>
      </div>
    </div>
  );
}

interface AttentionItem {
  id: string;
  title: string;
  sub: string;
  tone: "crit" | "warn" | "info";
  to: string;
}

export default function MobileDashboardPage() {
  const navigate = useNavigate();
  const { data: devices = [], statusCounts } = useDevices({ pageSize: 100 });
  const { policies } = useSoftwareUpdatePolicies();
  const inbox = useApprovalsInbox();
  const pendingApprovals = useMemo(
    () => (inbox.data ?? []).filter((r) => r.status === "PENDING"),
    [inbox.data],
  );

  const compliancePct = useMemo(() => {
    if (!devices.length) return 0;
    const compliant = devices.filter((d) => d.status === "active").length;
    return (compliant / devices.length) * 100;
  }, [devices]);

  const onlineCount = statusCounts.active;
  const pendingUpdates = policies.length;
  const criticalVulns = 0; // wired in Phase 2 when vuln hook is mobile-ready

  const attention: AttentionItem[] = useMemo(() => {
    const items: AttentionItem[] = [];
    pendingApprovals.slice(0, 2).forEach((a) =>
      items.push({
        id: a.requestId,
        title: humanizeAction(a.actionType),
        sub: `Awaiting your decision · ${a.riskTier === "TWO_PERSON" ? "Two-person" : "Approve"}`,
        tone: a.riskTier === "TWO_PERSON" ? "crit" : "warn",
        to: `/m/approvals/${a.requestId}`,
      }),
    );
    const offline = devices
      .filter((d) => d.status === "inactive")
      .slice(0, 2);
    offline.forEach((d) =>
      items.push({
        id: d.id,
        title: d.name,
        sub: `Offline · ${d.os_type || "device"}`,
        tone: "warn",
        to: `/m/devices/${d.id}`,
      }),
    );
    return items.slice(0, 4);
  }, [pendingApprovals, devices]);

  return (
    <div className="px-1 pb-6">
      <MobileAppHeader />

      <div className="px-3">
        <MCard className="mb-3 flex items-center gap-4 p-4">
          <ComplianceRing value={compliancePct} />
          <div className="min-w-0 flex-1">
            <h2 className="m-h3 text-[15px]">Fleet compliance</h2>
            <p className="m-sub">
              {statusCounts.active} of {statusCounts.total || 0} devices in
              line with policy
            </p>
            <MStatusPill tone={compliancePct >= 90 ? "ok" : "warn"} className="mt-2">
              {compliancePct >= 90 ? "Healthy" : "Needs attention"}
            </MStatusPill>
          </div>
        </MCard>

        <div className="mb-3 grid grid-cols-2 gap-2.5">
          <MTile
            value={onlineCount}
            label="Online"
            icon={<Wifi className="h-3.5 w-3.5" aria-hidden />}
            onClick={() => navigate("/m/devices")}
          />
          <MTile
            value={pendingUpdates}
            label="Update policies"
            tone={pendingUpdates ? "warn" : "default"}
            icon={<WrenchIcon className="h-3.5 w-3.5" aria-hidden />}
            onClick={() => navigate("/software-updates")}
          />
          <MTile
            value={criticalVulns}
            label="Critical vulns"
            tone={criticalVulns ? "crit" : "default"}
            icon={<ShieldAlert className="h-3.5 w-3.5" aria-hidden />}
            onClick={() => navigate("/m/security")}
          />
          <MTile
            value={pendingApprovals.length}
            label="Approvals"
            tone={pendingApprovals.length ? "warn" : "default"}
            icon={<Inbox className="h-3.5 w-3.5" aria-hidden />}
            onClick={() => navigate("/m/approvals")}
          />
        </div>

        <MSectionLabel>Needs your attention</MSectionLabel>
        {attention.length === 0 ? (
          <MEmpty
            title="All clear"
            description="No critical items right now."
            icon={<ShieldAlert className="h-4 w-4" aria-hidden />}
          />
        ) : (
          <div className="m-card overflow-hidden">
            {attention.map((item, i) => (
              <button
                key={item.id}
                type="button"
                onClick={() => navigate(item.to)}
                className={cn(
                  "flex w-full items-center gap-3 px-3.5 py-3 text-left tap-feedback",
                  i > 0 && "border-t border-[color:var(--m-line)]",
                )}
              >
                <span
                  className={cn(
                    "h-2 w-2 shrink-0 rounded-full",
                    item.tone === "crit" && "bg-[color:var(--m-crit)]",
                    item.tone === "warn" && "bg-[color:var(--m-warn)]",
                    item.tone === "info" && "bg-[color:var(--m-info)]",
                  )}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[12.5px] font-semibold text-[color:var(--m-ink)]">
                    {item.title}
                  </span>
                  <span className="block truncate text-[11px] text-[color:var(--m-gray-3)]">
                    {item.sub}
                  </span>
                </span>
                <ChevronRight className="h-4 w-4 text-[color:var(--m-gray-3)]" aria-hidden />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
